import { InvalidateQueryFilters, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { QueryTag } from '@common/ts';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import axiosInstance from '@/axios/axiosInstance';
import Routes from '@/router/routes';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import {
  RoleChange,
  ProjectProps,
  AiSearchResultsSubset,
  FiltersDataToSendProps,
  MoveProjectFormData,
  CopyProjectFormData,
} from '@/types';
import { API_ENDPOINTS } from '@/api-endpoints';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { updateOrgWorkspaceCookies } from '@/utils/cookieHelpers';

export const useMutateApi = <TData, TVariables>(
  mutationFn: (data: TVariables) => Promise<TData>,
  queryKeysToInvalidate?: Array<string | readonly unknown[]>,
  onSuccessCallback?: (data: TData) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      if (queryKeysToInvalidate && queryKeysToInvalidate.length > 0) {
        queryKeysToInvalidate.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }
      if (onSuccessCallback) {
        onSuccessCallback(data);
      }

      return data;
    },
  });
};

export const useMutateApiWithStore = <TData, TVariables = void>(
  mutationFn: (data: TVariables) => Promise<TData>,
  queryKeysToInvalidate?: string[],
  updateStore?: (data: TData) => void,
  onSuccessCallback?: (data: TData) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      if (queryKeysToInvalidate && queryKeysToInvalidate.length > 0) {
        queryKeysToInvalidate.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }

      if (updateStore) {
        updateStore(data);
      }

      if (onSuccessCallback) {
        onSuccessCallback(data);
      }

      return data;
    },
  });
};

export const useMutateUserOrganizationInvitation = (organization_id?: string) => {
  return useMutateApi(
    (formData: IformValues) =>
      axiosInstance.post(API_ENDPOINTS.ORGANIZATIONS.INVITE_USER(organization_id), formData),
    [QueryTag.ORGANIZATION_MEMBERS],
  );
};

export const useMutateUserWorkspaceInvitation = (
  organization_id?: string,
  workspace_id?: string,
) => {
  return useMutateApi(
    (formData: IformValues) =>
      axiosInstance.post(API_ENDPOINTS.WORKSPACES.SHARE(organization_id, workspace_id), formData),
    [QueryTag.WORKSPACE_MEMBERS],
  );
};

export const useMutateTransferWorkspaceOwnership = (
  organization_id?: string,
  workspace_id?: string,
) => {
  return useMutateApi(
    (formData: ITransferOwnershipProps) =>
      axiosInstance.post(
        API_ENDPOINTS.WORKSPACES.TRANSFER_OWNERSHIP(organization_id, workspace_id),
        formData,
      ),
    [QueryTag.WORKSPACE_GROUPS],
  );
};

export const useMutationsUpdateUser = () => {
  return useMutateApi((formData: TUpdateUserDto) =>
    axiosInstance.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, formData),
  );
};

export const useMutationsDeleteUser = () => {
  return useMutateApi(() => axiosInstance.delete(API_ENDPOINTS.USERS.DELETE_ACCOUNT));
};

export const useMutationsChangeRole = (organization_id?: string) => {
  return useMutateApi(
    (data: RoleChange) =>
      axiosInstance.patch(API_ENDPOINTS.ORGANIZATIONS.UPDATE_MEMBER_ROLE(organization_id), data),
    [QueryTag.ORGANIZATION_MEMBERS],
  );
};

export const useMutationsChangeRoleWorkspace = (
  organization_id?: string,
  workspace_id?: string,
) => {
  return useMutateApi(
    (data: IRoleChangeWorkspace) =>
      axiosInstance.put(API_ENDPOINTS.WORKSPACES.UPDATE_ROLE(organization_id, workspace_id), data),
    [QueryTag.WORKSPACE_MEMBERS],
  );
};

export const useMutateCreateWorkspace = (organizationId?: string) => {
  const navigate = useNavigate();
  return useMutateApi(
    (formData: { name: string }) =>
      axiosInstance.post(API_ENDPOINTS.WORKSPACES.CREATE(organizationId), formData),
    [QueryTag.WORKSPACE_GROUPS],
    (success) => {
      navigate(
        `/organization/${success?.data?.organization_id}${Routes.workspace.path}/${success?.data?._id}`,
      );
    },
  );
};

export const useMutateCreateProject = (organizationId?: string, workspaceId?: string) => {
  const navigate = useNavigate();
  return useMutateApi(
    (formData: { title: string; workspaceId?: string }) =>
      axiosInstance.post(API_ENDPOINTS.PROJECTS.CREATE(organizationId, workspaceId), formData),
    [QueryTag.PROJECTS, QueryTag.WORKSPACE_GROUPS],
    (success) => {
      navigate(
        `/organization/${organizationId}${Routes.workspace.path}/${workspaceId}${Routes.project_design.path}/${success?.data?._id}`,
      );
    },
  );
};

export const useMutateRenameWorkspace = (organizationId?: string, workspaceId?: string) => {
  return useMutateApi(
    ({ name }: { name: string }) =>
      axiosInstance.put(API_ENDPOINTS.WORKSPACES.UPDATE(organizationId, workspaceId), {
        name,
        _id: workspaceId,
      }),
    [QueryTag.WORKSPACE_GROUPS],
  );
};

export const useMutateRenameOrganization = () => {
  return useMutateApi(
    ({
      formData,
      organization_id,
    }: {
      formData: { name?: string; isHidden?: boolean };
      organization_id?: string;
    }) => axiosInstance.patch(API_ENDPOINTS.ORGANIZATIONS.UPDATE(organization_id), formData),
    [QueryTag.ORGANIZATIONS],
  );
};

export const useMutateDeleteOrganization = (organizationId: string) => {
  return useMutateApi(
    () => axiosInstance.delete(API_ENDPOINTS.ORGANIZATIONS.DELETE(organizationId)),
    [QueryTag.ORGANIZATIONS, QueryTag.USER],
  );
};

export const useMutateRemoveUserFromWorkspace = (organization_id?: string, workspace?: string) => {
  return useMutateApi(
    ({ user_id }: { user_id: string }) =>
      axiosInstance.delete(
        API_ENDPOINTS.WORKSPACES.REMOVE_MEMBER(organization_id, workspace, user_id),
      ),
    [QueryTag.WORKSPACE_GROUPS],
  );
};

export const useMutateMoveProject = (organizationId?: string, workspaceId?: string) => {
  return useMutateApi(
    (formData: MoveProjectFormData) =>
      axiosInstance.patch(
        API_ENDPOINTS.PROJECTS.MOVE(organizationId, workspaceId, formData.projectID),
        formData,
      ),
    [QueryTag.WORKSPACE_GROUPS, QueryTag.PROJECTS],
  );
};

export const useMutateCopyProject = (organizationId?: string, workspaceId?: string) => {
  return useMutateApi(
    (formData: CopyProjectFormData) =>
      axiosInstance.post(
        API_ENDPOINTS.PROJECTS.COPY(organizationId, workspaceId, formData.projectID),
        formData,
      ),
    [QueryTag.WORKSPACE_GROUPS, QueryTag.PROJECTS],
  );
};

export const useMutateProjectUpdate = (
  organizationId?: string,
  workspaceId?: string,
  projectId?: string,
) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (formData: Partial<ProjectProps>) => {
      return axiosInstance.put(
        API_ENDPOINTS.PROJECTS.UPDATE(organizationId, workspaceId, projectId),
        formData,
      );
    },
    [QueryTag.SINGLE_PROJECT_DATA],
    (responseData) => {
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};

export const useMutatePublishProject = (organization_id?: string, workspace_id?: string) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (formData) =>
      axiosInstance.post(API_ENDPOINTS.PROJECTS.PUBLISH(organization_id, workspace_id, formData)),
    QUERY_KEYS.PROJECTS_DATA,
    (responseData) => {
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};

export const useMutateRequestPublishProject = (organization_id?: string, workspace_id?: string) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (formData) =>
      axiosInstance.post(
        API_ENDPOINTS.PROJECTS.REQUEST_PUBLISH(organization_id, workspace_id, formData.project_id),
        { ...formData.selected_users },
      ),
    QUERY_KEYS.PROJECTS_DATA,
    (responseData) => {
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};

export const useMutateApprovePublishingProject = (
  organization_id?: string,
  workspace_id?: string,
) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (formData) =>
      axiosInstance.post(
        API_ENDPOINTS.PROJECTS.APPROVE_PUBLISH(organization_id, workspace_id, formData),
        { ...formData.selected_users },
      ),
    QUERY_KEYS.PROJECTS_DATA,
    (responseData) => {
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};

export const useMutateDeleteTellet = (organization_id?: string, workspace_id?: string) => {
  const {
    actions: { clearCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (formData) =>
      axiosInstance.delete(
        API_ENDPOINTS.PROJECTS.DELETE(organization_id, workspace_id, formData.targettedProjectID),
      ),
    [QueryTag.WORKSPACE_GROUPS, QueryTag.PROJECTS],
    () => {
      clearCurrentProject();
    },
  );
};

export const useMutateDeleteWorkspace = (organization_id?: string, workspace_id?: string) => {
  return useMutateApi(
    () => axiosInstance.delete(API_ENDPOINTS.WORKSPACES.DELETE(organization_id, workspace_id)),
    [QueryTag.WORKSPACE_GROUPS],
  );
};

export const useMutateCompleteTellet = (
  organization_id?: string,
  workspace_id?: string,
  project_id?: string,
) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    () =>
      axiosInstance.post(
        API_ENDPOINTS.PROJECTS.COMPLETE(organization_id, workspace_id, project_id),
      ),
    [QueryTag.WORKSPACE_GROUPS],
    (responseData) => {
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};

export const useMutateResendEmailInvitation = () => {
  return useMutateApi((email: string | null) =>
    axiosInstance.post(API_ENDPOINTS.USERS.RESEND_VERIFICATION_EMAIL, {
      email,
    }),
  );
};

export const useMutateRenameTellet = (
  organizationId?: string,
  workspaceId?: string,
  projectId?: string,
) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (formData) =>
      axiosInstance.post(
        API_ENDPOINTS.PROJECTS.RENAME(organizationId, workspaceId, projectId),
        formData,
      ),
    [QueryTag.PROJECTS],
    (responseData) => {
      // Update store with the renamed project
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};

export const useMutateReinviteToOrg = (organization_id?: string) => {
  return useMutateApi(
    (formData) =>
      axiosInstance.post(API_ENDPOINTS.ORGANIZATIONS.RESEND_INVITATION(organization_id), formData),
    [QueryTag.ORGANIZATION_MEMBERS],
  );
};

export const useMutateCancelOrgInvitation = (organization_id?: string) => {
  return useMutateApi(
    (formData) =>
      axiosInstance.delete(API_ENDPOINTS.ORGANIZATIONS.CANCEL_INVITATION(organization_id), {
        data: formData,
      }),
    ['organization_members'],
  );
};

export const useMutateTransferOrganizationalOwnership = () => {
  return useMutateApi(
    (formData) =>
      axiosInstance.post(
        API_ENDPOINTS.ORGANIZATIONS.TRANSFER_OWNERSHIP(formData.organization_id),
        formData,
      ),
    ['roles_data_organization', QueryTag.ORGANIZATION_MEMBERS],
  );
};

export const useMutateCreateProjectTheme = (organization_id?: string) => {
  return useMutateApi(
    (formData) => axiosInstance.post(API_ENDPOINTS.THEMES.CREATE(organization_id), formData),
    [QueryTag.THEMES],
  );
};

export const useMutateUpdateProjectTheme = (organization_id?: string) => {
  return useMutateApi(
    ({ formData, themeId }) =>
      axiosInstance.put(API_ENDPOINTS.THEMES.UPDATE(organization_id, themeId), formData),
    [QueryTag.THEME, QueryTag.THEMES],
  );
};

export const useMutateDeleteProjectTheme = (organization_id?: string) => {
  return useMutateApi(
    (themeId: string | null) =>
      axiosInstance.delete(API_ENDPOINTS.THEMES.DELETE(organization_id, themeId)),
    [QueryTag.THEMES],
  );
};

export const useMutateRequestAnotherAccountInvitation = (organization_id?: string) =>
  useMutateApi(
    ({ email }: { email: string }) =>
      axiosInstanceNoAuth.post(API_ENDPOINTS.ORGANIZATIONS.NOTIFY_INVITER(organization_id), {
        email,
      }),
    [],
  );

export const useMutationsGoToOtherOrganization = () => {
  return useMutation({
    mutationFn: async (data: { org_id: string; workspace_id: string }) => {
      const response = await axiosInstance.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, {
        last_organization: data?.org_id,
        last_workspace: data?.workspace_id,
      });

      updateOrgWorkspaceCookies(response?.data?.last_organization, response?.data?.last_workspace);
    },
  });
};

/**
 * Verify two-factor authentication code from authenticator app
 */
export const useMutationVerifyTwoFactor = () => {
  return useMutation<LoginResponse, Error, { code: string; userId: string }>({
    mutationFn: (data) => axiosInstance.post(API_ENDPOINTS.USERS.VERIFY_2FA, data),
  });
};

/**
 * Generate new two-factor authentication secret and QR code
 */
export const useMutationGenerateTwoFactorSecret = () => {
  return useMutation<TwoFactorSetup, Error>({
    mutationFn: () => axiosInstance.post(API_ENDPOINTS.USERS.GENERATE_2FA, {}),
  });
};

/**
 * Enable two-factor authentication with token verification
 */
export const useMutationEnableTwoFactor = () => {
  return useMutation<BackupCodes, Error, Enable2FA>({
    mutationFn: (data) => axiosInstance.post(API_ENDPOINTS.USERS.ENABLE_2FA, data),
  });
};

/**
 * Disable two-factor authentication (requires password verification)
 */
export const useMutationDisableTwoFactor = () => {
  return useMutation<void, Error, Disable2FA>({
    mutationFn: (data) => axiosInstance.post(API_ENDPOINTS.USERS.DISABLE_2FA, data),
  });
};

// ANALYZERS

export const useMutateReRunAnalyze = () => {
  return useMutateApi(
    ({ project_id, f_id }: { project_id: string | undefined; f_id?: string | null }) =>
      axiosInstanceAnalyze.post(API_ENDPOINTS.RUN.START_ANALYSIS(project_id, f_id)),
  );
};

export const useMutateDeleteFilter = () => {
  return useMutateApi(
    ({ project_id, filter_id }: { project_id: string | undefined; filter_id?: string | null }) =>
      axiosInstanceAnalyze.delete(API_ENDPOINTS.FILTERS.DELETE(project_id, filter_id)),
    [QueryTag.PROJECT_FILTERS],
  );
};

export const useMutateDeleteConversations = () => {
  const {
    currentProject,
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    ({
      project_id,
      conversationIds,
    }: {
      project_id: string | undefined;
      conversationIds: string[];
    }) =>
      axiosInstanceAnalyze.delete(API_ENDPOINTS.RESULTS.DELETE_CONVERSATIONS(project_id), {
        data: { conversationIds },
      }),
    [QueryTag.SINGLE_PROJECT_DATA, 'single_conversations_data'],
    (response) => {
      const { deletedCount } = response?.data || {};

      // Update only the deleted onversations count
      if (currentProject && deletedCount) {
        const updatedProject = {
          ...currentProject,
          deleted_conversations: (currentProject.deleted_conversations || 0) + deletedCount,
        };
        setCurrentProject(updatedProject);
      }
    },
  );
};

export const useMutateCreateFilter = () => {
  return useMutateApi(
    ({ project_id, data }: { project_id?: string; data: FiltersDataToSendProps }) =>
      axiosInstanceAnalyze.post(API_ENDPOINTS.FILTERS.CREATE(project_id!), data),
    [QueryTag.NEW_CONVERSATIONS_COUNT, QueryTag.PROJECT_FILTERS],
  );
};

export const useMutateEditFilter = () => {
  return useMutateApi(
    ({
      project_id,
      filter_id,
      data,
    }: {
      project_id?: string;
      filter_id: string | null;
      data: FiltersDataToSendProps;
    }) => axiosInstanceAnalyze.put(API_ENDPOINTS.FILTERS.UPDATE(project_id!, filter_id), data),
    [QueryTag.PROJECT_FILTERS, QueryTag.NEW_CONVERSATIONS_COUNT],
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useMutateAiSearchAskQuestion = (
  filter_id: string | null,
  conversation_selected: string | null,
  searchData?: AiSearchResultsSubset,
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({
      project_id,
      inputQuestion,
    }: {
      project_id: string | undefined;
      inputQuestion: string;
    }) => {
      const response = await axiosInstanceAnalyze.post(
        `${API_ENDPOINTS.AI_SEARCH.CREATE(project_id)}${
          filter_id ? `?filter_id=${filter_id}` : ''
        }`,
        {
          question: inputQuestion,
        },
      );

      const params = new URLSearchParams();
      params.set('ai_search_question', response.data._id);

      const totalItems = searchData?.searches?.length || 1;
      const lastPage = Math.ceil(totalItems / 10);

      params.set('ai_search_question_page', String(lastPage));

      if (filter_id) {
        params.set('filter_id', filter_id);
      }
      if (conversation_selected) {
        params.set('selected_transcript', conversation_selected);
      }

      navigate(`?${params.toString()}`);

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryTag.AI_SEARCH_RESULTS] as InvalidateQueryFilters);
    },
  });
};

export const useMutateDeleteAskedQuestion = () => {
  return useMutateApi(
    ({
      projectId,
      searchId,
      filterId,
    }: {
      projectId?: string;
      searchId: string;
      filterId: string | null;
    }) =>
      axiosInstanceAnalyze.delete(API_ENDPOINTS.AI_SEARCH.DELETE(projectId), {
        data: { search_id: searchId, filter_id: filterId },
      }),
    [QueryTag.AI_SEARCH_RESULTS],
  );
};

export const useMutateGenerateCategories = () => {
  return useMutateApi(
    ({
      projectId,
      formData,
      categoryType,
      questionId,
      conversationsNumber,
    }: {
      projectId: string;
      questionId: string;
      formData: CategoryData[];
      categoryType: string | null;
      conversationsNumber: number;
    }) =>
      axiosInstanceAnalyze.post(
        API_ENDPOINTS.CATEGORIES.GENERATE(projectId, categoryType, conversationsNumber),
        {
          question_id: questionId,
          categories: formData,
        },
      ),
    [QueryTag.INTERVIEW_QUESTION_CATEGORIES],
  );
};

export const useMutateDeleteCategories = () => {
  return useMutateApi(
    ({
      projectId,
      questionId,
      categoryType,
    }: {
      projectId: string;
      questionId: string;
      categoryType: string | null;
    }) =>
      axiosInstanceAnalyze.delete(
        `${API_ENDPOINTS.CATEGORIES.DELETE_BY_QUESTION(
          projectId,
          questionId,
        )}?category_type=${categoryType}`,
      ),
    [QueryTag.INTERVIEW_QUESTION_CATEGORIES],
  );
};

export const useMutateGenerateAiCategories = () => {
  return useMutateApi(
    ({ projectID, questionID }: { projectID?: string; questionID?: string }) =>
      axiosInstanceAnalyze.post(API_ENDPOINTS.CATEGORIES.GENERATE_AI(projectID, questionID)),
    [QueryTag.INTERVIEW_QUESTION_CATEGORIES],
  );
};

export const useMutateOverrideCategories = () => {
  return useMutateApi(
    ({
      project_id,
      questionID,
      transformedData,
    }: {
      project_id?: string;
      questionID?: string;
      transformedData: {
        conversation_id: string;
        categories: {
          id: string | undefined;
        }[];
      }[];
    }) =>
      axiosInstanceAnalyze.put(
        API_ENDPOINTS.CATEGORIES.UPDATE_BY_QUESTION(project_id!, questionID),
        transformedData,
      ),
    [QueryTag.INTERVIEW_QUESTION_CATEGORIES, QueryTag.QUESTION_TRANSCRIPTS],
  );
};

export const useMutateGenerateTranscriptsTranslation = (projectId?: string) => {
  return useMutateApi(
    (data) =>
      axiosInstanceAnalyze.post(API_ENDPOINTS.RESULTS.TRANSLATE_CONVERSATIONS(projectId), data),
    [[QUERY_KEYS.PROJECT_LANGUAGES(projectId)] as InvalidateQueryFilters],
  );
};

export const useMutateDeleteMedia = (
  questionId: string,
  organizationId?: string,
  workspaceId?: string,
  projectId?: string,
) => {
  const {
    actions: { setCurrentProject },
  } = useCurrentProject();

  return useMutateApiWithStore(
    (data) =>
      axiosInstance.delete(
        API_ENDPOINTS.PROJECTS.DELETE_MEDIA(organizationId, workspaceId, projectId, questionId),
        { data },
      ),
    [QueryTag.SINGLE_PROJECT_DATA],
    (responseData) => {
      // Update store with the updated project data after media deletion
      if (responseData?.data) {
        setCurrentProject(responseData.data as ProjectProps);
      }
    },
  );
};
